import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

/**
 * Lazy in-browser thumbnail factory: one shared offscreen WebGL renderer frames
 * and snapshots each GLTF to a PNG data-URL on demand. Results are cached so a
 * given asset renders only once per session. No extra tooling, no many-canvas
 * WebGL context limits.
 */

let renderer = null;
let scene = null;
let camera = null;
let loader = null;

const cache = new Map();
const inflight = new Map();
let queue = Promise.resolve();

function init() {
  if (renderer) return;
  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
  renderer.setSize(160, 160);
  renderer.setPixelRatio(1);
  renderer.setClearColor(0x000000, 0);

  scene = new THREE.Scene();
  scene.add(new THREE.AmbientLight(0xffffff, 1.15));
  const key = new THREE.DirectionalLight(0xffffff, 1.5);
  key.position.set(3, 5, 4);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xffffff, 0.55);
  fill.position.set(-3, 2, -4);
  scene.add(fill);

  camera = new THREE.PerspectiveCamera(35, 1, 0.01, 2000);
  loader = new GLTFLoader();
}

function renderOne(url) {
  return new Promise((resolve) => {
    try {
      init();
      loader.load(
        url,
        (gltf) => {
          try {
            const obj = gltf.scene;
            const box = new THREE.Box3().setFromObject(obj);
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());
            obj.position.sub(center);

            const holder = new THREE.Group();
            holder.add(obj);
            scene.add(holder);

            const maxDim = Math.max(size.x, size.y, size.z) || 1;
            const dist = maxDim * 2.1;
            camera.position.set(dist * 0.85, dist * 0.7, dist * 0.95);
            camera.lookAt(0, 0, 0);
            camera.near = maxDim / 100;
            camera.far = maxDim * 12;
            camera.updateProjectionMatrix();

            renderer.render(scene, camera);
            const dataURL = renderer.domElement.toDataURL('image/png');

            scene.remove(holder);
            obj.traverse((o) => {
              if (o.isMesh) {
                o.geometry?.dispose?.();
                if (Array.isArray(o.material)) o.material.forEach((m) => m?.dispose?.());
                else o.material?.dispose?.();
              }
            });
            resolve(dataURL);
          } catch (e) {
            resolve(null);
          }
        },
        undefined,
        (err) => { resolve(null); }
      );
    } catch (e) {
      resolve(null);
    }
  });
}

export function getThumbnail(url) {
  if (!url) return Promise.resolve(null);
  if (cache.has(url)) return Promise.resolve(cache.get(url));
  if (inflight.has(url)) return inflight.get(url);
  const p = (queue = queue.then(() => renderOne(url))).then((d) => {
    cache.set(url, d);
    inflight.delete(url);
    return d;
  });
  inflight.set(url, p);
  return p;
}

function renderObject(obj) {
  try {
    init();
    const box = new THREE.Box3().setFromObject(obj);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    obj.position.sub(center);
    const holder = new THREE.Group();
    holder.add(obj);
    scene.add(holder);
    const maxDim = Math.max(size.x, size.y, size.z) || 1;
    const dist = maxDim * 2.1;
    camera.position.set(dist * 0.85, dist * 0.7, dist * 0.95);
    camera.lookAt(0, 0, 0);
    camera.near = maxDim / 100;
    camera.far = maxDim * 12;
    camera.updateProjectionMatrix();
    renderer.render(scene, camera);
    const dataURL = renderer.domElement.toDataURL('image/png');
    scene.remove(holder);
    return dataURL;
  } catch (e) {
    return null;
  }
}

// Native assets: thumbnail from a three.js builder (shared offscreen renderer).
export function getNativeThumbnail(id, buildFn) {
  if (!id || !buildFn) return Promise.resolve(null);
  const key = `native:${id}`;
  if (cache.has(key)) return Promise.resolve(cache.get(key));
  if (inflight.has(key)) return inflight.get(key);
  const p = (queue = queue.then(() => Promise.resolve(renderObject(buildFn())))).then((d) => {
    cache.set(key, d);
    inflight.delete(key);
    return d;
  });
  inflight.set(key, p);
  return p;
}
